import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CollaborationType } from "@/types/collaborations";

export const useCollaborationTypes = (brandId: string | null) => {
  const [types, setTypes] = useState<CollaborationType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTypes = async () => {
    if (!brandId) {
      setTypes([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_collabs_types')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTypes((data || []) as CollaborationType[]);
    } catch (error) {
      console.error('Error fetching collaboration types:', error);
      setTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createType = async (typeData: Partial<CollaborationType>) => {
    if (!brandId) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('os_collabs_types')
        .insert([{
          name: typeData.name || '',
          description: typeData.description,
          prerequisites: typeData.prerequisites,
          icon: typeData.icon,
          status: typeData.status || 'active',
          metadata: typeData.metadata,
          brand_id: brandId,
          user_id: user.id,
        }])
        .select()
        .single();

      if (error) throw error;
      setTypes(prev => [data as CollaborationType, ...prev]);
      return data as CollaborationType;
    } catch (error) {
      console.error('Error creating collaboration type:', error);
      return null;
    }
  };

  const updateType = async (id: string, updates: Partial<CollaborationType>) => {
    try {
      const { data, error } = await supabase
        .from('os_collabs_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setTypes(prev => prev.map(t => t.id === id ? data as CollaborationType : t));
      return data as CollaborationType;
    } catch (error) {
      console.error('Error updating collaboration type:', error);
      return null;
    }
  };

  const deleteType = async (id: string) => {
    try {
      const { error } = await supabase
        .from('os_collabs_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTypes(prev => prev.filter(t => t.id !== id));
      return true;
    } catch (error) {
      console.error('Error deleting collaboration type:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchTypes();
  }, [brandId]);

  return {
    types,
    isLoading,
    refetch: fetchTypes,
    createType,
    updateType,
    deleteType,
  };
};
